// src/components/Visualsearch/Visualsearch.js
import React, { useState, useRef, useEffect } from 'react';
import './Visualsearch.css';
import { useNavigate } from 'react-router-dom';
import LazyLoad from 'react-lazyload';

const VisualSearch = ({ products }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [matchedProducts, setMatchedProducts] = useState([]);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(null);
  const [geminiInsights, setGeminiInsights] = useState(null);
  const [useGeminiAnalysis, setUseGeminiAnalysis] = useState(true);
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

  // ✅ REMOVED: Don't load model-viewer script again (already loaded globally)
  // The script is already loaded in your index.html or App.js

  // ✅ GEMINI VISION API - Same pattern as AIAssistant.js
  const analyzeImageWithGemini = async (imageData) => {
    if (!GEMINI_API_KEY) {
      console.warn('Gemini API key not configured');
      return null;
    }

    const base64Data = imageData.split(',')[1];

    const prompt = `Analyze this image for an e-commerce AR platform. Identify:
1. Product category (furniture/decor/electronics/vehicle/art)
2. Visual features (colors, style, material)
3. Price range estimate
4. Product keywords for search

Respond with ONLY valid JSON (no markdown):
{
  "category": "furniture",
  "subcategory": "chair",
  "visualFeatures": {
    "dominantColors": ["brown", "beige"],
    "style": "modern",
    "material": "wood",
    "estimatedSize": "medium"
  },
  "priceRange": {
    "min": 100,
    "max": 500,
    "confidence": "medium"
  },
  "matchingKeywords": ["chair", "furniture", "wooden", "modern"],
  "shoppingAdvice": "Consider furniture that matches this aesthetic",
  "qualityIndicators": ["quality design", "durable"]
}`;

    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-pro-vision:generateContent?key=${GEMINI_API_KEY}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{
              parts: [
                { text: prompt },
                {
                  inline_data: {
                    mime_type: 'image/jpeg',
                    data: base64Data
                  }
                }
              ]
            }],
            generationConfig: {
              temperature: 0.4,
              topK: 32,
              topP: 1,
              maxOutputTokens: 2048
            },
            safetySettings: [
              {
                category: "HARM_CATEGORY_HARASSMENT",
                threshold: "BLOCK_MEDIUM_AND_ABOVE"
              },
              {
                category: "HARM_CATEGORY_HATE_SPEECH",
                threshold: "BLOCK_MEDIUM_AND_ABOVE"
              }
            ]
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Gemini API Error:', errorData);
        return null;
      }

      const data = await response.json();
      const analysisText = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

      let cleanText = analysisText.trim()
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .trim();

      try {
        return JSON.parse(cleanText);
      } catch (parseError) {
        console.error('JSON Parse Error:', cleanText);
        const fallbackCategory = extractCategoryFromText(analysisText);
        return {
          category: fallbackCategory || 'General',
          subcategory: 'Product',
          visualFeatures: {
            dominantColors: ['neutral'],
            style: 'modern',
            material: 'mixed',
            estimatedSize: 'medium'
          },
          priceRange: {
            min: 50,
            max: 300,
            confidence: 'low'
          },
          matchingKeywords: [fallbackCategory || 'product'],
          shoppingAdvice: 'Browse our catalog for similar items',
          qualityIndicators: ['quality product']
        };
      }
    } catch (error) {
      console.error('Gemini analysis error:', error);
      return null;
    }
  };

  const extractCategoryFromText = (text) => {
    const lowerText = text.toLowerCase();
    const categories = {
      'furniture': ['furniture', 'chair', 'table', 'sofa', 'bed', 'desk'],
      'decor': ['decor', 'decoration', 'art', 'painting', 'wall'],
      'electronics': ['electronics', 'laptop', 'computer', 'phone', 'appliance'],
      'vehicle': ['vehicle', 'car', 'automobile'],
      'art': ['art', 'painting', 'sculpture']
    };

    for (const [category, keywords] of Object.entries(categories)) {
      if (keywords.some(keyword => lowerText.includes(keyword))) {
        return category;
      }
    }
    return 'General';
  };

  const analyzeImageBasic = async (imageFile, isCamera = false) => {
    await new Promise(resolve => setTimeout(resolve, 2500));

    const detectedFeatures = {
      room: 'Living Room',
      style: 'Modern',
      colors: ['Gray', 'White', 'Blue'],
      furniture: ['Chair', 'Sofa', 'Table'],
      space: 'Medium',
      lighting: 'Bright'
    };

    return detectedFeatures;
  };

  const analyzeImage = async (imageFile, isCamera = false) => {
    setIsAnalyzing(true);

    let imageDataURL;
    if (!isCamera) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result);
      };
      reader.readAsDataURL(imageFile);
      
      imageDataURL = await new Promise((resolve) => {
        const r = new FileReader();
        r.onload = (e) => resolve(e.target.result);
        r.readAsDataURL(imageFile);
      });
    } else {
      setUploadedImage(imageFile);
      imageDataURL = imageFile;
    }

    let geminiAnalysis = null;
    let basicAnalysis = null;

    if (useGeminiAnalysis && GEMINI_API_KEY) {
      try {
        geminiAnalysis = await analyzeImageWithGemini(imageDataURL);
        if (geminiAnalysis) {
          setGeminiInsights(geminiAnalysis);
        }
      } catch (error) {
        console.warn('Gemini analysis failed, using basic analysis');
      }
    }

    if (!geminiAnalysis) {
      basicAnalysis = await analyzeImageBasic(imageFile, isCamera);
      setAnalysisResults(basicAnalysis);
    } else {
      setAnalysisResults({
        room: geminiAnalysis.subcategory || 'Room',
        style: geminiAnalysis.visualFeatures?.style || 'Modern',
        colors: geminiAnalysis.visualFeatures?.dominantColors || ['Neutral'],
        furniture: geminiAnalysis.matchingKeywords || ['Furniture'],
        space: geminiAnalysis.visualFeatures?.estimatedSize || 'Medium',
        lighting: 'Bright'
      });
    }

    let results = [];

    if (geminiAnalysis) {
      results = products.filter(product => {
        const productText = `${product.name} ${product.category || ''} ${product.description || ''}`.toLowerCase();

        if (geminiAnalysis.category && product.category && 
            product.category.toLowerCase().includes(geminiAnalysis.category.toLowerCase())) {
          return true;
        }

        if (geminiAnalysis.matchingKeywords) {
          return geminiAnalysis.matchingKeywords.some(keyword =>
            productText.includes(keyword.toLowerCase())
          );
        }

        if (geminiAnalysis.visualFeatures?.style && 
            productText.includes(geminiAnalysis.visualFeatures.style.toLowerCase())) {
          return true;
        }

        return false;
      });
    } else if (basicAnalysis) {
      results = products.filter(product => {
        const productText = `${product.name} ${product.category || ''} ${product.description || ''}`.toLowerCase();

        const furnitureMatch = basicAnalysis.furniture.some(item =>
          productText.includes(item.toLowerCase())
        );
        const styleMatch = productText.includes(basicAnalysis.style.toLowerCase());
        const colorMatch = basicAnalysis.colors.some(color =>
          productText.includes(color.toLowerCase())
        );

        return furnitureMatch || styleMatch || colorMatch;
      });
    }

    const scoredResults = results.map(product => {
      const analysisData = geminiAnalysis || basicAnalysis;
      return {
        ...product,
        matchScore: calculateMatchScore(product, analysisData, !!geminiAnalysis),
        matchReason: getMatchReason(product, analysisData, !!geminiAnalysis)
      };
    }).sort((a, b) => b.matchScore - a.matchScore);

    setMatchedProducts(scoredResults);
    setIsAnalyzing(false);
  };

  const calculateMatchScore = (product, features, isGemini) => {
    let score = 0;
    const productText = `${product.name} ${product.category || ''} ${product.description || ''}`.toLowerCase();

    if (isGemini) {
      if (features.category && product.category && 
          product.category.toLowerCase() === features.category.toLowerCase()) {
        score += 50;
      }

      if (features.matchingKeywords) {
        features.matchingKeywords.forEach(keyword => {
          if (productText.includes(keyword.toLowerCase())) score += 15;
        });
      }

      if (features.visualFeatures?.style && 
          productText.includes(features.visualFeatures.style.toLowerCase())) {
        score += 20;
      }

      if (features.visualFeatures?.material && 
          productText.includes(features.visualFeatures.material.toLowerCase())) {
        score += 15;
      }
    } else {
      if (features.furniture) {
        features.furniture.forEach(furniture => {
          if (productText.includes(furniture.toLowerCase())) score += 30;
        });
      }

      if (features.style && productText.includes(features.style.toLowerCase())) {
        score += 25;
      }

      if (features.colors) {
        features.colors.forEach(color => {
          if (productText.includes(color.toLowerCase())) score += 15;
        });
      }
    }

    return Math.min(score, 100);
  };

  const getMatchReason = (product, features, isGemini) => {
    const reasons = [];
    const productText = `${product.name} ${product.category || ''}`.toLowerCase();

    if (isGemini) {
      if (features.category && productText.includes(features.category.toLowerCase())) {
        reasons.push(`Matches ${features.category} category`);
      }
      if (features.visualFeatures?.style && productText.includes(features.visualFeatures.style.toLowerCase())) {
        reasons.push(`${features.visualFeatures.style} style match`);
      }
      if (features.matchingKeywords && features.matchingKeywords.length > 0) {
        const matchedKeyword = features.matchingKeywords.find(k => productText.includes(k.toLowerCase()));
        if (matchedKeyword) reasons.push(`Matches ${matchedKeyword}`);
      }
    } else {
      if (features.furniture) {
        features.furniture.forEach(furniture => {
          if (productText.includes(furniture.toLowerCase())) {
            reasons.push(`Matches ${furniture} in your room`);
          }
        });
      }

      if (features.style && productText.includes(features.style.toLowerCase())) {
        reasons.push(`${features.style} style match`);
      }

      if (features.colors) {
        features.colors.forEach(color => {
          if (productText.includes(color.toLowerCase())) {
            reasons.push(`${color} color harmony`);
          }
        });
      }
    }

    return reasons.length > 0 ? reasons[0] : 'Recommended for your space';
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      analyzeImage(file);
    }
  };

  const handleCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();

      await new Promise(resolve => {
        video.onloadedmetadata = resolve;
      });

      setTimeout(() => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        
        const imageData = canvas.toDataURL('image/jpeg');
        stream.getTracks().forEach(track => track.stop());
        
        analyzeImage(imageData, true);
      }, 3000);
    } catch (error) {
      console.error('Camera access denied:', error);
      alert('📸 Camera access denied. Please allow camera permissions or use file upload instead.');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      analyzeImage(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const resetSearch = () => {
    setUploadedImage(null);
    setAnalysisResults(null);
    setMatchedProducts([]);
    setAiAnalysisLoading(null);
    setGeminiInsights(null);
  };

  const analyzeFeatureWithAI = async (featureName) => {
    setAiAnalysisLoading(featureName);
    await new Promise(resolve => setTimeout(resolve, 1500));

    let aiResponse = '';

    if (geminiInsights) {
      switch(featureName) {
        case 'room':
          aiResponse = `AI detected a ${geminiInsights.subcategory || 'room'} with ${geminiInsights.visualFeatures?.style || 'modern'} style. ${geminiInsights.shoppingAdvice || ''}`;
          break;
        case 'style':
          aiResponse = `The ${geminiInsights.visualFeatures?.style || 'modern'} style is characterized by ${geminiInsights.qualityIndicators?.join(', ') || 'quality design'}. Perfect match for your space!`;
          break;
        case 'colors':
          const colors = geminiInsights.visualFeatures?.dominantColors || ['neutral'];
          aiResponse = `AI color analysis detected ${colors.join(', ')} as dominant colors. This palette creates a harmonious atmosphere.`;
          break;
        case 'space':
          aiResponse = `The ${geminiInsights.visualFeatures?.estimatedSize || 'medium'} space detected. Estimated price range: $${geminiInsights.priceRange?.min || 50} - $${geminiInsights.priceRange?.max || 300}.`;
          break;
        default:
          aiResponse = 'AI analysis complete.';
      }
    } else {
      switch(featureName) {
        case 'room':
          aiResponse = `This appears to be a ${analysisResults.room}. AI detected furniture arrangements typical of this space, with ${analysisResults.furniture.join(', ')} visible.`;
          break;
        case 'style':
          aiResponse = `The ${analysisResults.style} style is characterized by clean lines and functional design.`;
          break;
        case 'colors':
          aiResponse = `AI color analysis detected ${analysisResults.colors.join(', ')} as dominant colors.`;
          break;
        case 'space':
          aiResponse = `The room dimensions suggest a ${analysisResults.space} space. This size is ideal for ${analysisResults.furniture.length} key furniture pieces.`;
          break;
        default:
          aiResponse = 'AI analysis complete.';
      }
    }

    alert(aiResponse);
    setAiAnalysisLoading(null);
  };

  // ✅ FIXED: Render 3D Model or fallback to image (no duplicate script loading)
  const renderProductPreview = (product, isHovered) => {
    // Check if product has 3D models
    if (product.modelSrc || product.iOSSrc) {
      return (
        <LazyLoad height={300} offset={100} once>
          <model-viewer
            src={product.modelSrc}
            ios-src={product.iOSSrc}
            alt={product.name}
            ar
            ar-modes="webxr scene-viewer quick-look"
            camera-controls
            auto-rotate={isHovered}
            rotation-per-second="30deg"
            shadow-intensity="1"
            environment-image="neutral"
            exposure="1"
            style={{
              width: '100%',
              height: '300px',
              backgroundColor: '#ecf0f3',
              borderRadius: '15px 15px 0 0',
            }}
          >
            <div className="model-loading-overlay" slot="poster">
              <div className="loading-spinner"></div>
              <p>Loading 3D Model...</p>
            </div>
          </model-viewer>
        </LazyLoad>
      );
    }

    // Fallback to image if no 3D model
    return (
      <img 
        src={product.image || '/placeholder-product.png'} 
        alt={product.name}
        style={{
          width: '100%',
          height: '300px',
          objectFit: 'cover',
          borderRadius: '15px 15px 0 0',
        }}
      />
    );
  };

  return (
    <div className="visual-search-container">
      <div className="visual-search-header">
        <h1>🎨 Gemini 3 Visual Search</h1>
        <p>Upload a photo of your room and let AI find matching products</p>
        
        <div className="gemini-toggle">
          <label>
            <input 
              type="checkbox" 
              checked={useGeminiAnalysis} 
              onChange={(e) => setUseGeminiAnalysis(e.target.checked)}
            />
            <span>🤖 Use Gemini AI Analysis {GEMINI_API_KEY ? '✅' : '⚠️ (API key missing)'}</span>
          </label>
        </div>
      </div>

      {!uploadedImage && (
        <div 
          className="upload-section"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <div className="upload-area">
            <div className="upload-icon">📸</div>
            <h3>Drag & drop or click to browse, or use your camera</h3>
            
            <div className="upload-buttons">
              <label className="upload-btn">
                📁 Browse Files
                <input 
                  ref={fileInputRef}
                  type="file" 
                  accept="image/*" 
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </label>
              
              <button 
                className="camera-btn"
                onClick={handleCameraCapture}
              >
                📷 Use Camera
              </button>
            </div>
            
            <p className="upload-hint">Supports: JPG, PNG, WebP (Max 10MB)</p>
          </div>
        </div>
      )}

      {isAnalyzing && (
        <div className="analyzing-overlay">
          <div className="analyzing-spinner"></div>
          <p>🔍 Detecting style, colors, and furniture...</p>
          {useGeminiAnalysis && GEMINI_API_KEY && (
            <p className="gemini-badge">⚡ Powered by Gemini Vision AI</p>
          )}
        </div>
      )}

      {uploadedImage && analysisResults && (
        <div className="results-section">
          <button className="reset-btn" onClick={resetSearch}>
            ✕ Clear & Search Again
          </button>

          <div className="analysis-container">
            <div className="uploaded-image-preview">
              <img src={uploadedImage} alt="Uploaded" />
            </div>

            <div className="analysis-details">
              <h3>🧠 AI Analysis Results</h3>
              
              {geminiInsights && (
                <div className="gemini-insights-panel">
                  <h4>⚡ Gemini Vision Insights</h4>
                  <div className="insight-grid">
                    <div className="insight-item">
                      <span className="insight-label">Category:</span>
                      <span className="insight-value">{geminiInsights.category}</span>
                    </div>
                    <div className="insight-item">
                      <span className="insight-label">Style:</span>
                      <span className="insight-value">{geminiInsights.visualFeatures?.style}</span>
                    </div>
                    <div className="insight-item">
                      <span className="insight-label">Material:</span>
                      <span className="insight-value">{geminiInsights.visualFeatures?.material}</span>
                    </div>
                    <div className="insight-item">
                      <span className="insight-label">Price Range:</span>
                      <span className="insight-value">
                        ${geminiInsights.priceRange?.min} - ${geminiInsights.priceRange?.max}
                      </span>
                    </div>
                  </div>
                  <div className="gemini-advice">
                    💡 {geminiInsights.shoppingAdvice}
                  </div>
                </div>
              )}

              <div className="feature-tags">
                <div 
                  className="feature-tag"
                  onClick={() => analyzeFeatureWithAI('room')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="feature-icon">🏠</span>
                  <div>
                    <p className="feature-label">Room Type</p>
                    <p className="feature-value">{analysisResults.room}</p>
                  </div>
                  {aiAnalysisLoading === 'room' && <span className="loading-dot">⏳</span>}
                </div>

                <div 
                  className="feature-tag"
                  onClick={() => analyzeFeatureWithAI('style')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="feature-icon">✨</span>
                  <div>
                    <p className="feature-label">Style</p>
                    <p className="feature-value">{analysisResults.style}</p>
                  </div>
                  {aiAnalysisLoading === 'style' && <span className="loading-dot">⏳</span>}
                </div>

                <div 
                  className="feature-tag"
                  onClick={() => analyzeFeatureWithAI('colors')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="feature-icon">🎨</span>
                  <div>
                    <p className="feature-label">Colors</p>
                    <p className="feature-value">{analysisResults.colors.join(', ')}</p>
                  </div>
                  {aiAnalysisLoading === 'colors' && <span className="loading-dot">⏳</span>}
                </div>

                <div 
                  className="feature-tag"
                  onClick={() => analyzeFeatureWithAI('space')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="feature-icon">📐</span>
                  <div>
                    <p className="feature-label">Space Size</p>
                    <p className="feature-value">{analysisResults.space}</p>
                  </div>
                  {aiAnalysisLoading === 'space' && <span className="loading-dot">⏳</span>}
                </div>
              </div>
            </div>
          </div>

          <div className="matched-products">
            <h3>
              ✅ Found {matchedProducts.length} matching products based on your room's style and needs
            </h3>

            <div className="products-grid">
              {matchedProducts.map((product) => (
                <div 
                  key={product.id} 
                  className="product-card-3d"
                  onClick={() => navigate(`/product/${product.id}`)}
                  onMouseEnter={() => setHoveredProductId(product.id)}
                  onMouseLeave={() => setHoveredProductId(null)}
                >
                  <div className="product-3d-preview">
                    {renderProductPreview(product, hoveredProductId === product.id)}
                    <div className="match-score-badge">
                      {product.matchScore}% Match
                    </div>
                    {(product.modelSrc || product.iOSSrc) && (
                      <div className="ar-badge-visual">🎯 AR Ready</div>
                    )}
                  </div>
                  
                  <div className="product-info">
                    <h4>{product.name}</h4>
                    <p className="match-reason">✨ {product.matchReason}</p>
                    <p className="product-price">${product.price}</p>
                    <button className="view-ar-btn" onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/product/${product.id}`);
                    }}>
                      {product.modelSrc ? '🎯 View in AR' : 'View Details'} →
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {matchedProducts.length === 0 && (
              <div className="no-results">
                <p>🔍 No exact matches found.</p>
                <p>Try uploading a different image or browse our catalog.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualSearch;
